import { Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule, NgModel } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { LoadingIndicatorDirective } from '@zero/shared/ui-directives';
import { LoginInfoCardsComponent, UserNotesComponent } from '@zero/shared/ui-web-components';
import { ButtonComponent } from '@zero/shared/ui-web-design-system';
import { AuthorizationData, ValidationPatterns } from '@zero/shared/util-models';
import { PreAuthorizationSessionAuth, RouteOptionsService } from '@zero/shared/util-web-app-services';
import { commonRoutes, URL_APPSTORE_APPLE, URL_APPSTORE_GOOGLE } from 'constants/web-app-constants';
import { firstValueFrom } from 'rxjs';
import { OAuthParameters } from '../models/oauth-parameters';
import { ServerAuthcodeData } from '../models/server-authcode-data';
import { OauthService } from '../services/oauth.service';
import { TradingSessionComponent } from '../trading-session/trading-session.component';
import { OriginClient } from './origin-client';

@Component({
  selector: 'login-oauth-login',
  templateUrl: './oauth-login.component.html',
  styleUrls: ['./oauth-login.component.scss'],
  imports: [
    LoadingIndicatorDirective,
    LoginInfoCardsComponent,
    FormsModule,
    RouterLink,
    TradingSessionComponent,
    TranslocoPipe,
    UserNotesComponent,
    ButtonComponent,
  ],
})
export class OauthLoginWebComponent implements OnInit, OnDestroy {
  public readonly ORIGIN_CLIENT_GENERIC = OriginClient.Generic;
  public readonly ORIGIN_CLIENT_TRADINGVIEW = OriginClient.TradingView;

  public username: string;
  public password: string;

  public passwordPattern: RegExp = ValidationPatterns.passwordPattern;
  public commonRoutes = commonRoutes;
  public validatedAtLeastOnce = false;
  public ongoingRequest = false;
  public sessionAuthorized: boolean;

  public URL_APPSTORE_APPLE = URL_APPSTORE_APPLE;
  public URL_APPSTORE_GOOGLE = URL_APPSTORE_GOOGLE;

  @ViewChild('userNameVar') public userNameInputRef: NgModel;

  public hasInitialized = false;
  public backendError: string;
  public authenticationData: AuthorizationData<PreAuthorizationSessionAuth>;
  #oAuthParameters: OAuthParameters;
  public originClient = OriginClient.Generic;

  readonly #oauthService = inject(OauthService);
  readonly #routeOptionsService = inject(RouteOptionsService);
  readonly #translateService = inject(TranslocoService);
  readonly #activatedRoute = inject(ActivatedRoute);

  async ngOnInit() {
    this.#routeOptionsService.hideHeaderAndFooter();
    this.#toggleBg();

    // OAuth 2 params that will be passed to the backend
    const params = this.#activatedRoute.snapshot.queryParamMap;
    this.#oAuthParameters = new OAuthParameters(
      params.get('client_id'),
      params.get('redirect_uri'),
      params.get('scope'),
      params.get('state'),
    );

    this.originClient = this.resolveClient(this.#oAuthParameters.clientId);

    if (!this.#oAuthParameters.isValid()) {
      this.backendError = await firstValueFrom(
        this.#translateService.selectTranslate('login.default.error.backendOAuthPreValidationError'),
      );
      return;
    }

    // Preload public key to increase execution time while logging in
    await this.#oauthService.initialize();
    this.hasInitialized = true;
  }

  ngOnDestroy(): void {
    this.#routeOptionsService.displayHeaderAndFooter();
    this.#toggleBg();
  }

  #toggleBg(): void {
    const { classList } = document.body;
    const cssClass = 'main-dark-blue';
    classList.contains(cssClass) ? classList.remove(cssClass) : classList.add(cssClass);
  }

  public async login(): Promise<void> {
    try {
      this.backendError = null;
      this.ongoingRequest = true;
      const response = await this.#oauthService.authenticate(this.username, this.password, this.#oAuthParameters);

      if (response.data.type === 'USER') {
        this.authenticationData = response as AuthorizationData<PreAuthorizationSessionAuth>;
      } else {
        const serverUserResponse = response as AuthorizationData<ServerAuthcodeData>;

        // Redirect server user to origins redirect-uri.
        window.location.href = `${this.#oAuthParameters.redirectUri}?code=${serverUserResponse.data.authCode}&state=${this.#oAuthParameters.state}`;
      }
    } catch (e) {
      console.error(e);
      this.backendError =
        e['statusText'] && e['statusText'] === 'Unknown Error'
          ? await firstValueFrom(this.#translateService.selectTranslate('login.default.error.backendOAuthInvalidParams'))
          : await firstValueFrom(this.#translateService.selectTranslate('login.default.error.backendGenericError'));
    }

    this.ongoingRequest = false;
  }

  public sessionAuthState(isAuthorized: boolean): void {
    if (isAuthorized) {
      // Redirect user to origins redirect-uri.
      window.location.href = `${this.#oAuthParameters.redirectUri}?code=${this.authenticationData.data.hash}&state=${this.#oAuthParameters.state}`;
    } else {
      // Authorization has been cancelled by user; showing login form again
      this.authenticationData = null;
    }
  }

  public resolveClient(clientId?: string): OriginClient {
    const tradingViewClientIds = [
      // t5
      '5fe1eb2so0stsb0tjc856ota2q',
      '5m726s1p07niv87obo0c79pre4',
      '4iiqp1ptdr76ppa06sv61sdpu4',
      // p1
      '3v9n28crhgao6guu1qbm4da8e6',
      '5r4ole65sc0iifdqjsl7dtfb5d',
      '6nbd29sb2dq9nmbo339v3p9460',
    ];

    if (tradingViewClientIds.includes(clientId)) {
      return OriginClient.TradingView;
    }

    return OriginClient.Generic;
  }
}
